import {get, has, isString, set} from 'lodash';
import * as moment from 'moment';
import {BlockType, Entry, Node, Property} from "../../contract/notion";
import {Options} from "../../contract/source";
import {ContentApiDef} from "../..";
import {findNode, propertyToBlock, transformBlocksSync} from "./helper";
import {transformer} from "./content";

type CreateSlugDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const createSlug: CreateSlugDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  // slug field has to be URL type
  const hasSlug = getUrlProperty(self, model, target, property, entry);
  if (!hasSlug) return null;

  // remove starting and trailing slash
  return hasSlug
    .replace(/^\//, "")
    .replace(/\/$/, "");
}

type GetDateTimeDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const getDateTime: GetDateTimeDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  const hasDate = get(entry.properties, property);
  if (!hasDate) return null;
  if (hasDate.type !== 'date')
    throw new Error(`Invalid type of slug field. Expected 'date', received '${hasDate.type}'.`)

  // remove starting and trailing slash
  return moment(hasDate.date.start).toISOString();
}

type GetPropertyDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const getProperty: GetPropertyDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  const hasProp = has(entry.properties, property) ? entry.properties[property] : null;

  return hasProp;
}

type GetRedirectDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => Entry|null;

export const getRedirect: GetRedirectDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  let redirect_slug;

  // (custom) processor
  const {find: f} = self.options.get(`${model}.transformer`);
  const find = (f || findNode).bind(null, self, model);

  // redirect field has to be a rollup type
  let hasRedirect: Property|boolean = true;
  while (hasRedirect) {
    hasRedirect = entry && (get(entry.properties, property) as Property);

    if (hasRedirect) {
      if (hasRedirect.type !== 'rollup')
        throw new Error(`Invalid type of slug field. Expected 'rollup', received '${hasRedirect.type}'.`)
      const {rollup: redirected} = hasRedirect;

      redirect_slug = redirected?.array.length && redirected.array[0];
      redirect_slug = redirect_slug?.url && redirect_slug.url.replace(/^\//, "").replace(/\/$/, "");

      if (redirect_slug) {
        entry = find(redirect_slug);
      } else {
        hasRedirect = false;
      }
    }
  }

  return entry;
}

type GetRelationsDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => Entry[];

export const getRelations: GetRelationsDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  const hasProp = get(entry.properties, property);
  if (!hasProp) return null;

  if (hasProp.type !== 'relation')
    throw new Error(`Invalid type of slug field. Expected 'relation', received '${hasProp.type}'.`)
  const {relation: entries} = hasProp;

  return (entries || []).map(({id}: {id: string}): string => id);
}

type GetTextPropertyDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const getTextProperty: GetTextPropertyDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  try {
    const {type: hasProp} = get(entry.properties, property);
    if (!hasProp || !has(entry.properties, property)) return null;

    const prop: Node[] = get(entry.properties[property as any], hasProp);
    return Array.isArray(prop)
      ? prop.map(({text}: Node): string => text.content).join()
      : null;
  } catch (e) {
    // @todo property not found, wrong type, or no input has been stored on the property
    console.error(e);
    return null;
  }
}

type GetRichTextPropertyDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const getRichTextProperty: GetRichTextPropertyDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  try {
    set(self, 'options, transformer.recursive', true);

    const {type: hasProp} = get(entry.properties, property);
    if (!hasProp || !has(entry.properties, property)) return null;

    entry.nodes = [];
    propertyToBlock(entry, get(entry.properties, property), BlockType.RICH_TEXT);
    const {nodes: blocks} = entry;

    const block = transformBlocksSync(self, transformer.bind(null, self), blocks, blocks[0], 0);

    if (block) {
      const {html} = block;

      return html;
    }

    return null;
  } catch (e) {
    // @todo property not found, wrong type, or no input has been stored on the property
    console.error(e);
    return null;
  }
}

type GetUrlPropertyDef = (
  self: ContentApiDef,
  model: string,
  target: string,
  property: string,
  entry: Entry
) => string|null;

export const getUrlProperty: GetUrlPropertyDef = (
  self,
  model,
  target,
  property,
  entry
) => {
  try {
    const {type: hasUrl} = get(entry.properties, property);
    if (!hasUrl || !has(entry.properties, property)) return null;

    const prop: string = get(entry.properties[property as string], hasUrl);

    return prop;
  } catch (e) {
    // @todo property not found, wrong type, or no input has been stored on the property
    return null;
  }
}

export type TransformEntryDef = (
  self: ContentApiDef|{options: Options},
  model: string,
  entries: Entry[]
) => Entry[];

export const transformEntry: TransformEntryDef = (
  self, // could not assign 'this' :(
  model,
  entries,
) => {
  // bootstrap
  const {
    find: f,
    slug: s,
    redirect: rd,
    datetime: d,
    text: t,
    prop: p,
    relations: rl,
    rich: ri,
    url: u,
    ...processors
  } = self.options.get(`${model}.transformer`);
  const Schema = self.options.get(`${model}.Schema`);

  // (custom-) processors
  const find = (f || findNode).bind(null, self, model);
  const slugify = (s || createSlug).bind(null, self, model);
  const redirectify = (rd || getRedirect).bind(null, self, model);
  const datetime = (d || getDateTime).bind(null, self, model);
  const relations = (rl || getRelations).bind(null, self, model);
  const rich = (ri || getRichTextProperty).bind(null, self, model);
  const url = (u || getUrlProperty).bind(null, self, model);
  const prop = (p || getProperty).bind(null, self, model);
  const text = (t || getTextProperty).bind(null, self, model);

  // get (special) properties and handlers
  Object.entries(Schema).map(([key, value]) => {
    // use a processor with a property
    if (isString(value)) {
      Schema[key] = text.bind(null, key, value);
    } else if (Array.isArray(value)) {
      const [type, property] = value.slice(value.length - 2); // last two values win

      switch (type) {
        case 'slug':
          Schema['slug'] = slugify.bind(null, key, property);
          return;
        case 'redirect':
          Schema['redirect'] = redirectify.bind(null, key, property);
          return;
        case 'date':
          Schema[key] = datetime.bind(null, key, property);
          return;
        case 'prop':
          Schema[key] = prop.bind(null, key, property);
          return;
        case 'relations':
          Schema[key] = relations.bind(null, key, property);
          return;
        case 'rich':
          Schema[key] = rich.bind(null, key, property);
          return;
        case 'url':
          Schema[key] = url.bind(null, key, property);
          return;
        default:
          // use a custom processor, or fall back to the default
          if (has(processors, type)) {
            Schema[key] = processors[type].bind(null, self, model, key, property)
          } else {
            Schema[key] = prop.bind(null, key, property);
          }
          return;
      }
    }
  });

  // create entries
  return entries.reduce((result, entry) => {
    const {slug: slugify, redirect, ...processors} = Schema;
    const slug = slugify(entry);
    const original_id = entry.id; // cache original entry id
    const original = Object.assign({}, entry); // no circular refs

    // bail if there is no slug
    if (!slug) return result;

    // check if entry redirects
    let redirect_id: {[key: string]: Entry|string} = null;
    if (redirect) entry = redirect(original);
    if (entry.id !== original_id) {
      redirect_id = {};
      redirect_id.original = original; // initial entry
      redirect_id.redirect_id = entry.id; // id from target entry
    }

    // after entry resolves, get entry properties
    const properties: { [key: string]: any } =
      Object.entries((processors || {})).reduce((result: { [key: string]: any }, [key, handler]: [string, any]) => {
        if (!key) return;

        result[key as any] = handler(entry);
        return result;
      }, {});

    // side-effect: update node map
    const node = find(slug);
    if (node) {
      Object.entries((properties || {})).map(([key, value]) => {
        if (!key) return;

        node[key] = value;
      });
      Object.entries((redirect_id || {})).map(([key, value]) => {
        if (!key) return;

        node[key] = value;
      });
    }

    result.push({
      ...entry,
      ...redirect_id,
      id: original_id,
      slug,
      ...properties,
    });

    return result;
  }, []);
}

export default (
  self: ContentApiDef|{options: Options},
  model = 'page'
): TransformEntryDef =>
  transformEntry.bind(
    null,
    self, // could not assign 'this' :(
    model
  );
